// @ts-nocheck
import { useEffect, useState } from 'react';
import axios from 'axios';
import utils from '../../utils/utils';
import useConsole from '../Console/useConsole';
import useDatasetGrid from '../useDatasetGrid';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';

const FTS_BASE_URL = ApiEndpoints.MODULE_BASE_URL.FTS;
const totalPageCount = 'x-total-count';

/**
 * @function useFeatureEngineering
 * @description hook for feature engineering module
 * @param {function} fetchModuleTasks - fetching module tasks
 * @param {function} fetchDatasetList - fetching dataset list
 * @param {object} datasetList - dataset list
 * @param {function} fetchDatasetRowsWithPagination - function for fetching dataset rows with pagination
 * @param {object} datasetRowsWithPagination - dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - setting dataset rows with pagination
 * @param {number} totalDatasetRows - total dataset rows
 * @param {number} datasetRowsPageCount - dataset rows page count
 * @param {boolean} isLoading - loading state
 * @param {function} updateDatasetCells - updating dataset cells
 * @param {object} project - project
 * @returns {object} -returning multiple methods and states for feature engineering module
 * @example const {fetchDatasetColumnDetails,fetchFEModuleTasks,fetchAsyncResult}
 * =useFeatureEngineering()
 */
export default function useFeatureEngineering() {
  const {
    fetchModuleTasks,
    fetchDatasetList,
    datasetList,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,
    updateDatasetCells,
    project,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    taskCount: gridTaskCount,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    gridOperationRerun,

    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
  } = useDatasetGrid();
  const {
    consoleModuleTasks,
    setConsoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();
  const { markDatasetAsReadyToUse } = useDataIngestion();

  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();

  // For Active Operation
  const [activeOperation, setActiveOperation] = useState({});

  // For Active Dataset
  const [activeDataset, setActiveDataset] = useState('');

  // For Column Details of Active Dataset
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  // Preliminary Steps Tasks
  const [moduleTasks, setModuleTasks] = useState([]);
  const [totalModuleTask, setTotalModuleTask] = useState(0);
  const [totalModuleTaskConsole, setTotalModuleTaskConsole] = useState(0);
  const [taskCount, setTaskCount] = useState({});

  // For paramters
  const [binningColumn, setBinningColumn] = useState({});

  // For ordinal column
  const [ordinalColumn, setOrdinalColumn] = useState({});

  const [biningID, setBiningID] = useState('0');

  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);

  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(`${FTS_BASE_URL + ApiEndpoints.FTS.GET_FE_TASKS}/${taskID}/${type}/`)
      .then((res) => {
        return res;
      });

  /**
   * @function updatebinningID
   * @description updating binning id
   * @returns {void}
   * @example updatebinningID()
   */
  const updatebinningID = () => {
    setBiningID(0);
  };
  // For Response Data : Graphs or Tables or Table ID
  const [taskDetails, updateTaskDetails] = useState({});
  /**
   * @function resetTaskDetails
   * @description reseting of task details
   * @returns {void}
   * @example resetTaskDetails()
   */
  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  // For fetching Active Dataset Columns
  /**
   * @function fetchDatasetColumnDetails
   * @description API call for fetching dataset column details
   * @param {string} datasetName - name of the dataset
   * @returns {Promise} AxiosPromise
   * @example  fetchDatasetColumnDetails(datasetName)
   */
  const fetchDatasetColumnDetails = (datasetName) =>
    axios
      .post(
        `${FTS_BASE_URL + ApiEndpoints.FTS.DATASET_COLUMN_DETAILS}`,
        datasetName
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res.data?.columns);
        },
        () => {}
      );

  // For Fetaching the Tasks List
  /**
   * @function fetchFEModuleTasks
   * @description fetching feature engineering module tasks
   * @param {number} projectId - project id
   * @param {boolean} hideLoaderOnRequest - hiding loader on request
   * @returns {void}
   * @example  fetchFEModuleTasks(projectId, hideLoaderOnRequest)
   */
  const fetchFEModuleTasks = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      return fetchModuleTasks(
        'FTS',
        MODULE_SUB_MODULE_OPERATIONS_TREE.FE.key,
        'GET_FE_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTask(res.headers?.[totalPageCount]);
          if (pageIndex > 1) {
            setModuleTasks((prevModuleTasks) => [
              ...prevModuleTasks,
              ...(res?.data?.tasks || []),
            ]);
          } else {
            setModuleTasks([...res.data.tasks]);
          }
        }
        return res;
      });
    }
  };
  /**
   * Fetches the count of module tasks.
   * @param {string} projectId - The ID of the project.
   * @param {boolean} hideLoaderOnRequest - Whether to hide the loader on request.
   * @param {number} count - The count of tasks to fetch.
   * @return {Promise} A promise that resolves with the count of tasks.
   */
  const fetchModuleTasksCount = (projectId, hideLoaderOnRequest, count) => {
    if (projectId) {
      fetchModuleTasks(
        'FTS',
        MODULE_SUB_MODULE_OPERATIONS_TREE.FE.key,
        'GET_FE_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        null,
        null,
        null,
        count
      ).then((res) => {
        setTaskCount(res?.data);
      });
    }
  };

  /**
   * @function fetchFEModuleTasksConsole
   * @description API call for fetching data quality module tasks
   * @param {number} projectId - module tasks of a particular project id
   * @param {boolean} hideLoaderOnRequest -hiding loader on request
   * @returns {void}
   * @example fetchFEModuleTasksConsole(project_id,hideLoaderOnRequest);
   */
  const fetchFEModuleTasksConsole = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.FE.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.FE.key,
        'GET_FE_TASKS',
        projectId,
        hideLoaderOnRequest,
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTaskConsole(res.headers[totalPageCount]);
          if (pageIndex > 1) {
            setConsoleModuleTasks([...consoleModuleTasks, ...res.data]);
          } else {
            setConsoleModuleTasks([...res.data]);
          }
        }
      });
    }
  };

  // Fetch Asnyc Operation Result
  /**
   * @function fetchAsyncResult
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   *  @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResult(taskID, additionTaskDetails, successCallback)
   */
  const fetchAsyncResult = (taskID, additionTaskDetails, successCallback) =>
    axios.get(`${FTS_BASE_URL + ApiEndpoints.FTS.GET_FE_TASKS}/${taskID}`).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          Object.values(
            MODULE_SUB_MODULE_OPERATIONS_TREE.FE.subModules.FE.operations
          ).filter((data) => {
            if (
              data.key === additionTaskDetails.operation &&
              res.data?.parameters?.dataset_id
            ) {
              datasetList.forEach((dataset) => {
                if (dataset.id === Number(res.data.parameters?.dataset_id)) {
                  setActiveDataset(dataset);
                }
              });
              updateTaskDetails(res.data);
              setActiveOperation(data);
              successCallback(res.data);
            }
            return null;
          });
        } else {
          updateTaskDetails({});
        }
      },
      () => {}
    );

  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project?.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.Name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );

  // For Terminating Task in Task Manager
  /**
   * @function  terminateTask
   * @description API call for terminate  task
   * @param {number}  taskID - task id
   * @returns  {Promise} AxiosPromise
   * @example  terminateTask(taskID)
   */
  const terminateTask = (taskID) =>
    axios
      .delete(`${`${FTS_BASE_URL + ApiEndpoints.FTS.GET_FE_TASKS}/${taskID}`}`)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });

  /**
   * @function ftsRerunOperation
   * @description API call for reruning the operation
   * @param {object} body - body
   * @returns {Promise} AxiosPromise
   * @example ftsRerunOperation(body);
   */
  const ftsRerunOperation = (body) => {
    return axios
      .post(`${FTS_BASE_URL + ApiEndpoints.FTS.RERUN}`, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function  numericTransformationRequest
   * @description API call for numeric Transformation Request
   * @param {object} body - data for numeric Transformation Request
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns  {Promise} AxiosPromise
   * @example  numericTransformationRequest(body,successCallback)
   */
  const numericTransformationRequest = (body, successCallback) =>
    axios.post(`${FTS_BASE_URL + ApiEndpoints.FTS.FTS_NUMERIC}`, body).then(
      () => {
        if (taskDetails?.results) {
          updateTaskDetails({
            ...taskDetails,
            results: {},
          });
        }
        successCallback();
        updatebinningID();
      },
      (error) => {
        throw error;
      }
    );
  /**
   * @function  categoricalTransformationRequest
   * @description API call for categorical Transformation Request
   * @param {object} body - data for categorical Transformation Request
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example categoricalTransformationRequest(body,successCallback)
   */
  const categoricalTransformationRequest = (body, successCallback) =>
    axios.post(`${FTS_BASE_URL + ApiEndpoints.FTS.FTS_CATEGORICAL}`, body).then(
      () => {
        if (taskDetails?.results) {
          updateTaskDetails({
            ...taskDetails,
            results: {},
          });
        }
        successCallback();
        updatebinningID();
      },
      (error) => {
        throw error;
      }
    );
  /**
  * @function timeSeriesTransformationRequest
  * @description API call for time Series Transformation Request
  * @param {object} body - data for time Series Transformation Request
  * @param {function} successCallback - Callback function to execute upon completion of API call
  happens during the calling of API
  * @returns {Promise} AxiosPromise
  * @example timeSeriesTransformationRequest(body,successCallback)
  */
  const timeSeriesTransformationRequest = (body, successCallback) =>
    axios
      .post(`${FTS_BASE_URL + ApiEndpoints.FTS.FTS_TIMESTAMP}`, body)
      .then(() => {
        if (taskDetails?.results) {
          updateTaskDetails({
            ...taskDetails,
            results: {},
          });
        }
        successCallback();
        updatebinningID();
      });
  /**
   * @function fetchBinningColumnDetails
   * @description API call for fetching Binning Column Detail
   * @param {object} body - data for fetching Binning Column Detail
   * @returns {Promise} AxiosPromise
   * @example fetchBinningColumnDetail(body)
   */
  const fetchBinningColumnDetails = (body) =>
    axios.post(`${FTS_BASE_URL + ApiEndpoints.FTS.GET_TARGET_LIST}`, body).then(
      (res) => {
        setBinningColumn(res.data.valid_targets);
      },
      () => {}
    );

  /**
   * @function fetchOrdinalColumnDetails
   * @description API call for fetching Ordinal Column Detail
   * @param {object} body - data for fetching Ordinal Column Detail
   * @returns {Promise} AxiosPromise
   * @example fetchOrdinalColumnDetails(datasetId,projectId)
   */
  const fetchOrdinalColumnDetails = (datasetId, projectId) => {
    axios
      .get(`${FTS_BASE_URL + ApiEndpoints.FTS.FETCH_UNIQUE_VALUES}`, {
        params: { dataset_id: datasetId, project_id: projectId },
      })
      .then((res) => {
        setOrdinalColumn(res.data);
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function markTaskReadUnreadFeatureEngineering
   * @description API call for mark Task Read Unread Feature Engineering
   * @param {object} taskParams - parameters of mark Task Read Unread Feature Engineering
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example markTaskReadUnreadFeatureEngineering(taskParams,successCallback,errorCallback)
   */
  const markTaskReadUnreadFeatureEngineering = (taskParams, successCallback) =>
    axios
      .get(`${FTS_BASE_URL + ApiEndpoints.FTS.FE_JOB_QUEUE_MARK_READ_UNREAD}`, {
        params: taskParams,
      })
      .then(
        () => {
          successCallback();
        },
        () => {}
      );

  return {
    activeOperation,
    setActiveOperation,

    activeDataset,
    setActiveDataset,

    datasetColumnDetails,
    fetchDatasetColumnDetails,

    moduleTasks,
    setModuleTasks,
    totalModuleTask,
    fetchFEModuleTasks,
    taskCount,
    fetchModuleTasksCount,

    taskDetails,
    resetTaskDetails,
    fetchAsyncResult,

    binningColumn,
    fetchBinningColumnDetails,

    ordinalColumn,
    fetchOrdinalColumnDetails,

    biningID,
    updatebinningID,

    categoricalTransformationRequest,
    timeSeriesTransformationRequest,
    numericTransformationRequest,

    datasetList,
    terminateTask,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,

    renameColumn,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    gridTaskCount,
    fetchGridTasks,
    terminateGridTask,
    fetchDatasetList,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    gridOperationRerun,

    analyse,
    ftsRerunOperation,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    markTaskReadUnreadFeatureEngineering,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,

    // console related
    consoleModuleTasks,
    totalModuleTaskConsole,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchFEModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    project,
    setConsoleAsyncTaskResults,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    markDatasetAsReadyToUse,
    fetchConsoleFileList,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
    fetchAsyncResultSplit,
  };
}
